<template>
  <div class="ct-workspace">
    <!-- Sin usos no hay nada que reintentar: las dos salidas reales son
         comprar o ganarlos con una misión. -->
    <b-modal
      ref="out-of-uses"
      centered
      hide-footer
      hide-header
      content-class="login-cta-modal"
    >
      <div class="text-center p-3">
        <h4 class="font-weight-bold mb-3">Te quedaste sin usos de corrección</h4>
        <p class="text-muted">
          Cada test corregido descuenta un uso. Puedes comprar más o ganarlos
          completando misiones.
        </p>
        <b-button pill variant="primary" size="lg" class="mb-2" @click="goToBuyUses">
          Comprar usos
        </b-button>
        <br />
        <b-button pill variant="outline-primary" @click="goToMissions">
          Ganar usos con misiones
        </b-button>
      </div>
    </b-modal>

    <SessionProgress
      :profession-id="professionId"
      :test-slug="testSlug"
      :session="session"
      @select-test="onTestChange"
    />

    <TestToolbar
      ref="toolbar"
      :profession-id="professionId"
      :test-slug="testSlug"
      :age="selectedAge"
      :show-age="activeTest && activeTest.requiresAge"
      :age-options="activeAgeOptions"
      :age-label="(activeTest && activeTest.ageLabel) || undefined"
      :age-placeholder="(activeTest && activeTest.agePlaceholder) || undefined"
      :primary-label="primaryActionLabel"
      :primary-disabled="primaryDisabled"
      :primary-loading="primaryLoading"
      :highlight-age="highlightAge"
      :highlight-test-select="highlightTestSelect"
      :session="session"
      :blocked-hint="blockedHint"
      :uses-chip="usesChip"
      :platform="requireStudent"
      @update:professionId="onProfessionChange"
      @update:testSlug="onTestChange"
      @update:age="onAgeChange"
      @age-interact="onAgeInteract"
      @primary-action="onPrimaryAction"
    >
      <template #toolbar-extra>
        <slot name="toolbar-extra" />
      </template>
    </TestToolbar>

    <TestFormRouter
      v-if="activeTest && !professionComplete"
      ref="formRouter"
      :key="formRouterKey"
      :test="activeTest"
      :age="selectedAge"
      :initial-answers="editingAnswers"
      @corrected="onTestCorrected"
      @toolbar-meta="onToolbarMeta"
    />

    <div v-if="professionComplete" class="ct-profession-complete">
      <p class="ct-profession-complete-text">
        Ya corregiste todos los tests de {{ professionLabel }} disponibles
        para este estudiante.
      </p>
      <div class="ct-profession-complete-actions">
        <!-- Sin botón de informe aquí: el host (CorrectTestsModule) ya muestra
             el único "ir a los documentos del estudiante" justo debajo, y en la
             plataforma el informe se crea allá, no se descarga desde el
             corrector como en el flujo a granel. -->
        <button
          type="button"
          class="ct-profession-complete-back"
          @click="$emit('go-back')"
        >
          Volver
        </button>
      </div>
    </div>

    <div
      v-if="activeTest && currentTestNeedsCorrection && !professionComplete"
      class="ct-bottom-correct"
    >
      <button
        type="button"
        class="ct-direct-btn"
        :disabled="primaryLoading"
        @click="onPrimaryAction"
      >
        {{ primaryLoading ? "Corrigiendo…" : primaryActionLabel }}
      </button>
      <p class="ct-bottom-correct-hint">
        Ingresa los puntajes y aprieta
        <strong>{{ primaryActionLabel }}</strong> para generar el informe.
      </p>
    </div>

    <ReportBottom
      v-if="activeTest && !currentTestNeedsCorrection && !requireStudent && !professionComplete"
      ref="reportBottom"
      :title="reportTitle"
      :subtitle="reportSubtitle"
      :test-ids="reportTestIds"
      :included-tests="reportIncludedTests"
      :can-download="canDownloadReport"
      :highlight-download="highlightDownload"
      :integrated="reportIsIntegrated"
      :report-mode="reportMode"
      :session="session"
      @downloaded="onReportDownloaded"
    />
  </div>
</template>

<script>
import TestToolbar from "./components/TestToolbar.vue";
import TestFormRouter from "./components/TestFormRouter.vue";
import ReportBottom from "./components/ReportBottom.vue";
import SessionProgress from "./components/SessionProgress.vue";
import { buildPostDownloadChat } from "./constants/chatMessages";
import {
  getPendingTestsForProfession,
  getCorrectedTestsForProfession,
  getCorrectedTestIdsForProfession,
  getCorrectedSessionEntriesForProfession,
} from "./utils/sessionProgress";
import {
  defaultSelection,
  getProfessionById,
  getTestBySlug,
  getTestByValue,
  getTestsForProfession,
  isProfessionAvailable,
} from "./constants/testCatalog";
import { createTestCorrectionSession } from "./utils/testCorrectionSession";
import {
  getFirstFieldIndex,
  initialGuidePhase,
} from "./utils/testCorrectionGuide";
import { scrollToElementDelayed } from "./utils/guidedScroll";
import { extractTestRecordId } from "./utils/normalizeTestIds";
import {
  hasCompleteIdtelAnswers,
  rangoEtarioToAge,
} from "./utils/idtelState";
import { TEST_FLOW } from "./constants/testCatalog";
import {
  setCorrectionStudent,
  setCorrectionSemester,
  setOutOfUsesHandler,
} from "@/services/testCorrectionAdapter";
import { usesChip } from "@/components/botpie-assistant/serviceUses";

export default {
  name: "CorregirTestWorkspace",
  components: { TestToolbar, TestFormRouter, ReportBottom, SessionProgress },
  provide() {
    return {
      corregirTestWorkspace: this,
    };
  },
  props: {
    // Set only by CorrectTestsModule (Utopie, in-platform). The free/
    // anonymous Shell renders this workspace with no props, so context stays
    // {} and studentGradeId undefined — the correction payload is byte-for-
    // byte identical to before this prop existed.
    context: {
      type: Object,
      default: () => ({}),
    },
    requireStudent: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    const initial = defaultSelection();
    return {
      session: createTestCorrectionSession({ granel: !this.requireStudent }),
      professionId: initial.professionId,
      testSlug: initial.testSlug,
      selectedAge: "",
      guidePhase: "age",
      formToolbarMeta: {
        label: "",
        disabled: false,
        loading: false,
      },
      highlightTestSelect: false,
      awaitingDownloadFocus: false,
      sessionRevision: 0,
      // Edad recordada por test (keep-alive conserva los puntajes; la edad la
      // gestiona el workspace, así que la restauramos al volver a un test).
      ageByTest: {},
      // Cambiarlo re-monta el router y limpia el caché de keep-alive (reset).
      formRouterKey: 0,
      // Respuestas de una corrección previa que el form activo debe precargar
      // (startEdit). null en el flujo normal, que siempre parte en blanco.
      editingAnswers: null,
    };
  },
  computed: {
    activeTest() {
      return getTestBySlug(this.testSlug);
    },
    currentTestCorrected() {
      return (
        this.activeTest &&
        this.session.isCorrected(this.activeTest.value)
      );
    },
    currentTestId() {
      if (!this.activeTest) return null;
      const entry = this.session.getCorrection(this.activeTest.value);
      return entry ? extractTestRecordId(entry) : null;
    },
    currentTestNeedsCorrection() {
      // Decide qué mostrar abajo: "Corregir test" (hay puntajes sin corregir)
      // vs "Descargar informe" (ya corregido y sin cambios pendientes).
      if (!this.activeTest) return false;
      if (!this.currentTestCorrected) return true;
      // Ya corregido: ¿se editó después? En tests con edad lo detectamos fiable
      // (la edad se resetea al cambiar de test, así que un re-ingreso queda
      // deshabilitado), y editar reactiva el botón de corregir.
      if (this.activeTest.requiresAge) {
        return (
          !this.formToolbarMeta.disabled && !this.formToolbarMeta.loading
        );
      }
      return false;
    },
    highlightAge() {
      return (
        this.guidePhase === "age" &&
        this.activeTest &&
        this.activeTest.requiresAge &&
        !this.selectedAge &&
        !this.currentTestCorrected
      );
    },
    highlightDownload() {
      if (this.awaitingDownloadFocus) return true;
      if (!this.reportTestIds.length) return false;
      return this.reportTestIds.some(
        (id) => !this.session.isDownloaded(id)
      );
    },
    reportIncludedTests() {
      void this.sessionRevision;
      return getCorrectedSessionEntriesForProfession(
        this.professionId,
        this.session
      );
    },
    reportTitle() {
      if (this.reportIsIntegrated) {
        return "Informe integrado listo para Word";
      }
      return this.activeTest
        ? this.activeTest.reportTitle
        : "Informe listo para Word";
    },
    reportSubtitle() {
      if (!this.activeTest) return "";
      if (this.reportIsIntegrated) {
        return (
          "Incluye " +
          this.reportIncludedLabels +
          ". Descarga un solo Word con todos los resultados de esta sesión."
        );
      }
      if (this.currentTestCorrected) {
        return "Ya puedes descargar el informe Word de este test.";
      }
      if (this.reportTestIds.length) {
        return (
          "Tienes tests corregidos en esta profesión. Puedes descargar el informe abajo."
        );
      }
      return "Corrige el protocolo para habilitar la descarga del informe.";
    },
    reportIncludedLabels() {
      return this.reportIncludedTests
        .map(function (entry) {
          return entry.label;
        })
        .join(", ");
    },
    isAuthenticated() {
      // Mismo token que api.js usa para autenticar el request: el store lo
      // mantiene sincronizado con localStorage (login/setUser). Se lee desde el
      // store y no de localStorage porque un computed sobre localStorage no
      // tiene dependencias reactivas y quedaría cacheado: al iniciar sesión sin
      // recargar (el modal de login), reportMode seguiría en "individual" y el
      // informe unificado prometido en el CTA nunca llegaría.
      return !!this.$store.state.login.token;
    },
    reportMode() {
      // Logueado: informe unificado granel por profesión (con fallback a
      // individual). Anónimo: solo el test recién corregido (individual) —
      // incentivo para iniciar sesión.
      return this.isAuthenticated ? "unified" : "individual";
    },
    reportTestIds() {
      void this.sessionRevision;
      if (!this.isAuthenticated) {
        return this.currentTestId ? [this.currentTestId] : [];
      }
      const ids = getCorrectedTestIdsForProfession(
        this.professionId,
        this.session
      );
      if (ids.length) return ids;
      if (this.currentTestId) return [this.currentTestId];
      return [];
    },
    reportIsIntegrated() {
      return this.reportTestIds.length > 1;
    },
    canDownloadReport() {
      return this.reportTestIds.length > 0;
    },
    activeAgeOptions() {
      return this.activeTest && this.activeTest.ageOptions
        ? this.activeTest.ageOptions
        : [];
    },
    primaryActionLabel() {
      if (this.formToolbarMeta.label) return this.formToolbarMeta.label;
      return this.activeTest
        ? this.activeTest.correctActionLabel
        : "Corregir test";
    },
    primaryLoading() {
      return this.formToolbarMeta.loading;
    },
    primaryDisabled() {
      if (!this.activeTest) return true;
      if (this.requireStudent && !this.context.studentGradeId) return true;
      if (this.session.isBlocked(this.activeTest.value)) return true;
      if (this.formToolbarMeta.disabled) return true;
      if (this.activeTest.requiresAge && !this.selectedAge) return true;
      return false;
    },
    // Saldo restante, visible mientras se llena el test. Hasta ahora lo único
    // que decía algo sobre los usos era el modal "Te quedaste sin usos", y es
    // reactivo: aparece con el 403, o sea después de llenar el protocolo
    // entero. El número tiene que llegar antes de que valga la pena.
    //
    // Se recalcula solo: `usesChip` lee store.state.login, y el socket
    // `planifications_uses_update` (layouts/utopie/Default.vue) reescribe los
    // cuatro saldos cuando el backend descuenta, así que al terminar una
    // corrección el chip baja sin que este componente pida nada.
    usesChip() {
      return usesChip("tests");
    },
    blockedHint() {
      if (!this.activeTest) return "";
      if (this.requireStudent && !this.context.studentGradeId) {
        return "Selecciona un curso y un estudiante para corregir el test.";
      }
      if (!this.session.isBlocked(this.activeTest.value)) return "";
      return "Este test no puede combinarse con otro de su grupo en la misma sesión.";
    },
    professionPendingTests() {
      void this.sessionRevision;
      return getPendingTestsForProfession(this.professionId, this.session);
    },
    // Utopie-only: once every test in the current profession is already
    // corrected for this student, there's nothing left to fill in — swap
    // the form area for a completion panel instead of leaving the visitor
    // on a stale "select the age" prompt for an already-done test (the free
    // Shell never hits this: its session always starts empty, and when it
    // does finish a profession, ReportBottom — its own "you're done" state
    // — already covers it).
    professionComplete() {
      return (
        this.requireStudent &&
        Boolean(this.context.studentGradeId) &&
        this.professionPendingTests.length === 0
      );
    },
    professionLabel() {
      const profession = getProfessionById(this.professionId);
      return profession ? profession.label : "este servicio";
    },
  },
  watch: {
    testSlug() {
      this.resetGuide();
    },
    "context.studentGradeId": {
      immediate: true,
      handler(studentGradeId) {
        setCorrectionStudent(studentGradeId == null ? null : studentGradeId);
      },
    },
    "context.semester": {
      immediate: true,
      handler(semester) {
        setCorrectionSemester(semester == null ? null : semester);
      },
    },
  },
  mounted() {
    this.resetGuide();
    this.$nextTick(() => this.scrollToActiveGuide());
    setOutOfUsesHandler(() => this.showOutOfUses());
  },
  beforeDestroy() {
    setCorrectionStudent(null);
    setCorrectionSemester(null);
    setOutOfUsesHandler(null);
  },
  methods: {
    showOutOfUses() {
      const modal = this.$refs["out-of-uses"];
      if (modal) modal.show();
    },
    // El corrector vive en /utopie#/ (producto a granel) y también dentro del
    // colegio (/redpie#/), que es otra SPA con otro router. Comprar usos y ver
    // las misiones son rutas de utopie, así que desde el colegio hay que
    // navegar por URL: un $router.push allá caería en su propio catch-all.
    // El discriminante es el base del router, no getMatchedComponents, que con
    // una ruta comodín siempre dice que sí.
    goToUtopieRoute(hash) {
      const modal = this.$refs["out-of-uses"];
      if (modal) modal.hide();
      const inUtopie = window.location.pathname.indexOf("/utopie") === 0;
      if (inUtopie) {
        this.$router.push(hash).catch(() => {});
        return;
      }
      window.location.href = "/utopie#" + hash;
    },
    goToBuyUses() {
      this.goToUtopieRoute("/buy_product/tests");
    },
    goToMissions() {
      this.goToUtopieRoute("/profile/missions");
    },
    resetGuide() {
      this.guidePhase = initialGuidePhase(this.activeTest);
      this.$nextTick(() => this.scrollToActiveGuide());
    },
    isFieldGuideActive(fieldIndex) {
      if (this.currentTestCorrected || this.guidePhase === "done") {
        return false;
      }

      if (fieldIndex === "edad") {
        return false;
      }

      if (this.guidePhase !== "field") return false;

      if (this.activeTest && this.activeTest.requiresAge && !this.selectedAge) {
        return false;
      }

      return fieldIndex === getFirstFieldIndex();
    },
    dismissFieldGuide() {
      if (this.currentTestCorrected || this.guidePhase === "done") return;
      if (this.guidePhase === "field") {
        this.guidePhase = "done";
      }
    },
    dismissAgeGuide() {
      if (this.currentTestCorrected || this.guidePhase === "done") return;
      if (this.guidePhase === "age") {
        this.guidePhase = "field";
      }
    },
    onAgeSelected() {
      if (this.guidePhase !== "age") return;
      this.guidePhase = "field";
      this.$nextTick(() => {
        this.$nextTick(() => this.scrollToActiveGuide());
      });
    },
    onAgeInteract() {
      this.dismissAgeGuide();
    },
    scrollToActiveGuide() {
      if (this.awaitingDownloadFocus) return;
      window.requestAnimationFrame(() => {
        const root = this.$el;
        if (!root) return;
        const fieldGuide = root.querySelector(
          [
            ".ct-form-rows .ct-guide-blink",
            ".ct-item-row.ct-guide-blink",
            ".ct-stsg-card.ct-guide-blink",
            ".ct-idtel-item-line.ct-guide-blink",
            ".ct-choice.ct-guide-blink",
            ".ct-choice-row.ct-guide-blink",
          ].join(", ")
        );
        const el =
          fieldGuide ||
          root.querySelector(".ct-guide-download") ||
          root.querySelector(".ct-toolbar-selects .ct-guide-blink");
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      });
    },
    scrollToDownloadButton() {
      scrollToElementDelayed(
        this.$refs.reportBottom &&
          this.$refs.reportBottom.getDownloadButtonEl(),
        58,
        0
      );
    },
    // Prefers a not-yet-corrected, not-blocked test in the profession, so
    // switching profession (or loading a student's prior history) lands on
    // actual pending work instead of a test that's already done — landing
    // on a done test with no restored age would otherwise show a stale
    // "select the age" prompt (age isn't part of what history hydration
    // restores).
    pickTestSlugForProfession(professionId) {
      const tests = getTestsForProfession(professionId);
      const pending = tests.find(
        (test) =>
          !this.session.isBlocked(test.value) &&
          !this.session.isCorrected(test.value)
      );
      if (pending) return pending.slug;
      const anyAvailable = tests.find((test) => !this.session.isBlocked(test.value));
      return anyAvailable ? anyAvailable.slug : tests[0].slug;
    },
    onProfessionChange(professionId) {
      this.professionId = professionId;
      this.awaitingDownloadFocus = false;
      this.highlightTestSelect = false;
      this.editingAnswers = null;
      this.testSlug = this.pickTestSlugForProfession(professionId);
      this.selectedAge = this.ageByTest[this.testSlug] || "";
      this.resetGuide();
      this.$emit("selection-change", this.selectionSnapshot());
    },
    onTestChange(testSlug) {
      this.testSlug = testSlug;
      this.awaitingDownloadFocus = false;
      this.highlightTestSelect = false;
      // Los puntajes precargados pertenecen al test que se estaba editando; al
      // cambiar de test dejan de aplicar (el form nuevo los ignoraría igual,
      // pero así no reaparecen si se vuelve a este test).
      this.editingAnswers = null;
      this.selectedAge = this.ageByTest[testSlug] || "";
      this.resetGuide();
      this.$emit("selection-change", this.selectionSnapshot());
    },
    onAgeChange(age) {
      this.selectedAge = age;
      this.$set(this.ageByTest, this.testSlug, age);
      if (age) this.onAgeSelected();
      this.$emit("selection-change", this.selectionSnapshot());
    },
    onPrimaryAction() {
      if (
        this.activeTest &&
        this.activeTest.requiresAge &&
        !this.selectedAge
      ) {
        this.guidePhase = "age";
        this.$nextTick(() => this.scrollToActiveGuide());
        if (this.$toasted) {
          const que = this.activeTest.ageLabel
            ? this.activeTest.ageLabel.toLowerCase()
            : "la edad";
          this.$toasted.error(`Selecciona ${que} antes de corregir.`);
        }
        return;
      }
      const router = this.$refs.formRouter;
      if (router) router.submit();
      this.$emit("primary-action", this.selectionSnapshot());
    },
    onToolbarMeta(meta) {
      this.formToolbarMeta = { ...this.formToolbarMeta, ...meta };
    },
    onTestCorrected(result) {
      if (!this.activeTest || !result) return;
      const testId = extractTestRecordId(result);
      if (!testId) {
        if (this.$toasted) {
          this.$toasted.error(
            "El test se corrigió pero no se recibió su ID. Recarga e intenta de nuevo."
          );
        }
        return;
      }
      this.session.upsertCorrection({
        id: testId,
        test: this.activeTest.value,
        name: this.activeTest.label,
      });
      this.sessionRevision += 1;
      this.guidePhase = "done";
      this.awaitingDownloadFocus = true;
      this.$emit("test-corrected", {
        test: this.activeTest,
        result,
        session: this.session,
      });
      this.$nextTick(() => {
        window.setTimeout(() => this.scrollToDownloadButton(), 150);
      });
    },
    onReportDownloaded() {
      this.awaitingDownloadFocus = false;
      // ReportBottom solo emite "downloaded" dentro del .then de la descarga,
      // así que acá el informe ya está en el disco de la persona. Se re-emite
      // para que el host sepa cuál es el único momento bueno para pedirle
      // opinión: acaba de recibir lo que vino a buscar.
      this.$emit("report-downloaded", {
        testIds: this.reportTestIds.slice(),
        integrated: this.reportIsIntegrated,
      });
      const pending = getPendingTestsForProfession(
        this.professionId,
        this.session
      );
      this.highlightTestSelect = pending.length > 0;
      this.emitChatSuggestion();
      if (pending.length > 0) {
        this.$nextTick(() => {
          const toolbar = this.$refs.toolbar;
          const testSelect =
            toolbar && toolbar.getTestSelectEl && toolbar.getTestSelectEl();
          if (testSelect) {
            window.setTimeout(() => {
              scrollToElementDelayed(testSelect, 72, 0);
            }, 600);
          }
        });
      }
    },
    emitChatSuggestion() {
      const pending = getPendingTestsForProfession(
        this.professionId,
        this.session
      );
      const pendingLabels = pending.map(function (test) {
        return test.label;
      });
      const correctedCount = getCorrectedTestsForProfession(
        this.professionId,
        this.session
      ).length;
      const payload = buildPostDownloadChat({
        testLabel: this.activeTest ? this.activeTest.label : "test",
        pendingLabels,
        correctedCount,
        standalone: Boolean(this.activeTest && this.activeTest.standalone),
      });
      this.$emit("chat-suggestion", payload);
    },
    selectionSnapshot() {
      return {
        professionId: this.professionId,
        testSlug: this.testSlug,
        testValue: this.activeTest ? this.activeTest.value : null,
        age: this.selectedAge,
        test: this.activeTest,
      };
    },
    resetSession() {
      this.session.reset();
      this.sessionRevision += 1;
      this.selectedAge = "";
      this.ageByTest = {};
      this.editingAnswers = null;
      // Re-monta el router: limpia el caché de keep-alive y todos los puntajes.
      this.formRouterKey += 1;
      this.awaitingDownloadFocus = false;
      this.highlightTestSelect = false;
      this.resetGuide();
    },
    // Public API for a host (CorrectTestsModule) to seed already-corrected
    // tests fetched from the student's history, without reaching into
    // `session` directly — mirrors how onTestCorrected upserts + bumps
    // sessionRevision so reportTestIds/reportIncludedTests pick it up.
    hydrateCorrections(entries) {
      if (!Array.isArray(entries) || !entries.length) return;
      entries.forEach((entry) => this.session.upsertCorrection(entry));
      this.sessionRevision += 1;
      // The active test may have just become "already corrected" (its age
      // wasn't restored, so its form would show a stale age prompt) — move
      // to the next pending test in the profession, if any.
      //
      // Solo en ese caso. El historial se pide de forma asíncrona al elegir
      // estudiante, así que esto llega con el profesional ya trabajando: si el
      // test activo sigue pendiente hay que dejarlo donde está, porque
      // onTestChange le cambia el test debajo y de paso le borra la edad que
      // acababa de elegir (selectedAge se relee de ageByTest, que está indexado
      // por slug).
      const activeStillPending =
        this.activeTest &&
        !this.session.isCorrected(this.activeTest.value) &&
        !this.session.isBlocked(this.activeTest.value);
      if (activeStillPending) return;
      const bestSlug = this.pickTestSlugForProfession(this.professionId);
      if (bestSlug !== this.testSlug) this.onTestChange(bestSlug);
    },
    dropCorrection(testValue) {
      this.session.removeCorrection(testValue);
      this.sessionRevision += 1;
    },
    // Public API: jump straight to a test by its TestEnum value, switching
    // profession if needed. Used when the host knows *which* test the
    // professional came for (editing a previous correction, or the "faltan
    // tests" modal naming the one the informe requires) — unlike
    // onProfessionChange, which picks whatever is pending.
    // Returns false when the value isn't in the catalog, so the caller can
    // fall back instead of silently landing on an unrelated test.
    selectTestByValue(testValue) {
      const test = getTestByValue(testValue);
      if (!test) return false;
      // Misma regla que el <select> de profesión de la toolbar: fuera del
      // colegio no se entra a una profesión platformOnly ni siquiera por esta
      // vía directa.
      if (!isProfessionAvailable(test.professionId, this.requireStudent)) {
        return false;
      }
      this.professionId = test.professionId;
      this.testSlug = test.slug;
      this.awaitingDownloadFocus = false;
      this.highlightTestSelect = false;
      this.editingAnswers = null;
      this.selectedAge = this.ageByTest[test.slug] || "";
      this.resetGuide();
      this.$emit("selection-change", this.selectionSnapshot());
      return true;
    },
    // Public API: re-open an already-corrected test with its previous answers
    // loaded, so the professional fixes one score instead of retyping the
    // whole protocol. `answers` is exactly what was POSTed to RedPsi
    // (get_previous_answers/), so its shape is the test's, not the form's.
    //
    // Correcting again creates a *new* Test row — there is no update path in
    // the backend. Retiring the old one is the host's job (delete_test on the
    // `corrected` event); this method only prepares the form.
    startEdit({ testValue, answers }) {
      const test = getTestByValue(testValue);
      if (!test) return false;
      if (
        test.flow === TEST_FLOW.IDTEL &&
        !hasCompleteIdtelAnswers(answers)
      ) {
        return false;
      }
      if (!this.selectTestByValue(testValue)) return false;
      // Sin esto el test sigue marcado como corregido: primaryDisabled lo deja
      // en gris (isBlocked) y, si era el último pendiente de la profesión,
      // professionComplete reemplaza el formulario por el panel de "ya
      // corregiste todo" — o sea, no habría nada que editar en pantalla.
      this.session.removeCorrection(testValue);
      this.sessionRevision += 1;
      this.editingAnswers = answers || null;
      this.restoreAgeFromAnswers(answers);
      // Los puntajes viejos ya están en pantalla: la guía que apunta al primer
      // campo vacío sobra y solo distrae.
      this.guidePhase = "done";
      // Re-monta el router para que el form corra su `created()` con
      // initialAnswers puesto (keep-alive conservaría el form en blanco).
      this.formRouterKey += 1;
      return true;
    },
    // La edad no viaja como estado propio del form sino dentro de las
    // respuestas: `edad` en casi todos, `Rango_Etario` del primer paso en
    // IDTEL. Restaurarla importa porque TestFormRouter no renderiza el form
    // (awaitingAge) mientras no haya edad seleccionada.
    restoreAgeFromAnswers(answers) {
      if (!answers || !this.activeTest || !this.activeTest.requiresAge) return;
      const step0 = answers["step-0"] || {};
      let value = "";
      if (answers.edad != null && answers.edad !== "") {
        value = String(answers.edad);
      } else if (step0.Rango_Etario) {
        value = rangoEtarioToAge(step0.Rango_Etario);
      }
      if (!value) return;
      // Si el valor guardado no es una de las opciones del test, dejar el
      // selector vacío: es preferible que el profesional elija a preseleccionar
      // una edad que el backend no reconoce.
      const known = (this.activeTest.ageOptions || []).some(
        (opt) => opt.value === value
      );
      if (!known) return;
      this.onAgeChange(value);
    },
    // Public API: abandon an in-progress edit without correcting. The host is
    // expected to reload the student's history afterwards, which puts the
    // still-active previous correction back into the session.
    cancelEdit() {
      this.editingAnswers = null;
      this.formRouterKey += 1;
    },
  },
};
</script>
